/** Display helpers for NWSS / wastewater signals in the precomputed grid and drawer. */

export type { NwssDrawerSampleScope } from "./nwssDrawerScope"
export { nwssScopePrefix, nwssScopedLabel } from "./nwssDrawerScope"

import type { NwssDrawerSampleScope } from "./nwssDrawerScope"
import { nwssScopePrefix, nwssScopedLabel } from "./nwssDrawerScope"

/** Stat band: both scopes visible when viewing full history. */
export const NWSS_HISTORIC_DETECTIONS_LABEL = "Historic detections"
export const NWSS_EVENT_DETECTIONS_LABEL = "Event detections"

export const NWSS_EVENT_DETECTION_TOOLTIP_LABEL = "Event detection"

export {
  NWSS_EVENT_DETECTION_ROW_LABEL,
  NWSS_RAW_TABLE_LEGEND_NOTE,
  NWSS_TAB_BY_COUNTY,
  NWSS_TAB_BY_SITE,
  NWSS_TAB_RAW_DATA,
} from "./nwssDrawerScope"

export const NWSS_SCOPE_HISTORIC_LABEL = "Historic"
export const NWSS_SCOPE_EVENT_LABEL = "Event"
export const NWSS_SCOPE_HISTORIC_TOOLTIP =
  "Full sample history loaded for this signal (padded window), including routine context samples."
export const NWSS_SCOPE_EVENT_TOOLTIP =
  "Only samples that define this anomaly event (event detections)."

export const nwssEntitySummaryCaption = (
  scope: NwssDrawerSampleScope
): string => {
  const daysNote =
    " Days since last detection are through the latest sample in this view."
  if (scope === "event") {
    return `${nwssScopePrefix(scope)} per-entity totals for detections that define this signal.${daysNote}`
  }
  return `${nwssScopePrefix(scope)} per-entity totals for the loaded sample history.${daysNote}`
}

export { nwssWeeklyByCountyTitle } from "./nwssDrawerScope"

export const nwssWeeklySiteTimelineChartName = (
  scope: NwssDrawerSampleScope
): string => nwssScopedLabel(scope, "weekly site detection timeline")

export const nwssRawSamplesTitle = (
  scope: NwssDrawerSampleScope,
  shown: number,
  total: number
): string => {
  const count =
    shown < total
      ? `${shown.toLocaleString()} of ${total.toLocaleString()}`
      : String(total)
  return `${nwssScopedLabel(scope, "raw samples")} (${count})`
}

const US_STATE_AND_TERRITORY_NAMES: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  PR: "Puerto Rico",
  GU: "Guam",
  VI: "U.S. Virgin Islands",
  AS: "American Samoa",
  MP: "Northern Mariana Islands",
}

export const PRECOMPUTED_WASTEWATER_SOURCE_LABEL = "Wastewater"

const expandStateToken = (token: string): string => {
  const abbrev = token.trim().toUpperCase()
  return US_STATE_AND_TERRITORY_NAMES[abbrev] ?? token
}

/** Spell out trailing state abbreviations (e.g. ``San Diego, CA`` → ``San Diego, California``). */
export const formatNwssResolvedLocation = (
  resolvedLocation: string | null | undefined
): string => {
  const raw = (resolvedLocation ?? "").trim()
  if (!raw) return ""

  const commaParts = raw.split(",").map(part => part.trim())
  if (commaParts.length >= 2) {
    const stateToken = commaParts.at(-1)
    if (!stateToken) return raw
    const geo = commaParts.slice(0, -1).join(", ")
    const stateName = expandStateToken(stateToken)
    return geo ? `${geo}, ${stateName}` : stateName
  }

  return expandStateToken(raw)
}

/** Drop the ``since [date]`` clause from cluster descriptions; event start is in the drawer. */
export const formatNwssSignalDescription = (
  description: string | null | undefined
): string => {
  const raw = (description ?? "").trim()
  if (!raw) return ""
  return raw.replace(/\s+since\s+\d{1,2}\/\d{1,2}\/\d{2}(?=\s+in\s+)/i, "")
}

export const formatNwssChartTitle = (
  disease: string | undefined,
  chartName: string,
  geo?: string,
  scope?: NwssDrawerSampleScope
): string => {
  const diseasePart = disease ?? "Wastewater"
  const scopedChartName =
    scope != null ? nwssScopedLabel(scope, chartName) : chartName
  if (geo?.trim()) {
    return `${diseasePart} · ${scopedChartName} · ${geo}`
  }
  return `${diseasePart} · ${scopedChartName}`
}

const nwssRollingChartMetricLabel = (scope?: NwssDrawerSampleScope): string => {
  if (scope === "event") {
    return nwssScopedLabel(scope, "14-day rolling detection count")
  }
  if (scope != null) {
    return nwssScopedLabel(scope, "14-day rolling detection rate")
  }
  return "14-Day Rolling Detection Rate"
}

export const formatNwssRollingRateChartTitle = (
  disease: string | undefined,
  geo: string,
  scope?: NwssDrawerSampleScope
): string => {
  const diseasePart = disease ?? "Wastewater"
  const location = formatNwssResolvedLocation(geo) || geo
  return `${diseasePart} ${nwssRollingChartMetricLabel(scope)} — ${location}`
}

export const nwssRollingChartYAxisLabel = (
  scope: NwssDrawerSampleScope
): string =>
  scope === "event"
    ? "14-Day Rolling Detection Count"
    : "14-Day Rolling Detection Rate"
